import { LUMINOSITY_STEPS, type FactionDef, type LuminosityStart } from '../board/types';
import { createRng } from '../rng/seededRandom';
import type { GameContext, GameState, LuminosityState, Piece } from './types';

/** Extra hit points a piece gets on a square that fully matches its affinity. */
export const LUMINOSITY_MAX_BONUS = 7;

/**
 * What a neutral side draws from the cycle (D-32): the same on every square, and what a Light or
 * Dark piece gets on a square halfway between the two. No square suits a neutral piece better
 * than another, so no turn of the cycle does either.
 */
export const NEUTRAL_LUMINOSITY_BONUS = Math.round(LUMINOSITY_MAX_BONUS / 2);

/** The original's start: the darker of the two middle phases, heading for Light's peak. */
export const CLASSIC_LUMINOSITY_START: LuminosityStart = { phase: 2, direction: 1 };

/** Salt for the start-of-game draw, so it never lines up with a fight roll or an AI tie-break. */
const START_DRAW_SALT = 0x6c756d69;

/** The reflection of a start: as far from the other peak, heading the other way. */
export function mirrorLuminosity(start: LuminosityStart): LuminosityStart {
  return { phase: LUMINOSITY_STEPS - 1 - start.phase, direction: start.direction === 1 ? -1 : 1 };
}

/**
 * Where the cycle stands when a game begins. Six steps have no neutral phase, and a fixed start
 * hands the opening rounds to whichever side the cycle heads for (about fifteen points of score
 * in the balance simulations), so unless the board or the caller names a start the seed picks
 * one of two mirror images: the original's start or its reflection. Over many games neither
 * side owns the opening; within one game the start is as deterministic as everything else. The
 * draw has its own stream so that a seed's fight rolls do not change with the start it gets.
 */
export function initialLuminosity(seed: number, start?: LuminosityStart): LuminosityState {
  if (start) return { phase: start.phase, direction: start.direction };
  const towardsLight = createRng(seed ^ START_DRAW_SALT).next() < 0.5;
  return towardsLight
    ? { ...CLASSIC_LUMINOSITY_START }
    : mirrorLuminosity(CLASSIC_LUMINOSITY_START);
}

/** One step of the cycle, bouncing at both ends. Happens after every full round. */
export function advanceLuminosity(l: LuminosityState): LuminosityState {
  const next = l.phase + l.direction;
  if (next < 0 || next >= LUMINOSITY_STEPS) {
    const direction = l.direction === 1 ? -1 : 1;
    return { phase: l.phase + direction, direction };
  }
  return { phase: next, direction: l.direction };
}

/** The Shift Time spell: reverse the flow, or jump to the opposite extreme when at a peak. */
export function shiftTime(l: LuminosityState): LuminosityState {
  if (l.phase === 0) return { phase: LUMINOSITY_STEPS - 1, direction: -1 };
  if (l.phase === LUMINOSITY_STEPS - 1) return { phase: 0, direction: 1 };
  return { phase: l.phase, direction: l.direction === 1 ? -1 : 1 };
}

/** Brightness of a cell right now, 0 (dark) … 1 (light). Void cells count as neutral. */
export function cellBrightness(ctx: GameContext, state: GameState, x: number, y: number): number {
  const cell = ctx.board.cells[y * ctx.board.width + x];
  switch (cell?.luminosity) {
    case 'light':
      return 1;
    case 'dark':
      return 0;
    case 'cycling':
      return state.luminosity.phase / (LUMINOSITY_STEPS - 1);
    default:
      return 0.5;
  }
}

/** Hit-point bonus for a faction affinity standing on a square of the given brightness. */
export function luminosityBonus(affinity: FactionDef['affinity'], brightness: number): number {
  if (affinity === 'light') return Math.round(LUMINOSITY_MAX_BONUS * brightness);
  if (affinity === 'dark') return Math.round(LUMINOSITY_MAX_BONUS * (1 - brightness));
  return NEUTRAL_LUMINOSITY_BONUS;
}

export function factionAffinity(ctx: GameContext, faction: string): FactionDef['affinity'] {
  return ctx.board.def.factions.find((f) => f.id === faction)?.affinity ?? 'neutral';
}

/**
 * Whether the cycle currently sits at the extreme that favours this piece's faction: that frees a
 * prisoner, and no piece can be imprisoned there. A neutral side has no such extreme; its
 * prisoners serve a fixed sentence instead (`NEUTRAL_SENTENCE`, D-32).
 */
export function isAtFavourablePeak(ctx: GameContext, state: GameState, piece: Piece): boolean {
  const affinity = factionAffinity(ctx, piece.faction);
  if (affinity === 'light') return state.luminosity.phase === LUMINOSITY_STEPS - 1;
  if (affinity === 'dark') return state.luminosity.phase === 0;
  return false;
}
